import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  Avatar,
  Snackbar,
  Badge,
  Stack,
} from "@mui/material";
import {
  Refresh,
  Visibility,
  RateReview,
  Publish,
  VisibilityOff,
  DeleteForever,
  Star,
  StarOutline,
  TuneRounded,
} from "@mui/icons-material";
import {
  getTestimonialStats,
  getTestimonialList,
  getTestimonialById,
  publishTestimonial,
  unpublishTestimonial,
  deleteTestimonial,
} from "../../../context/Testimonials";
import TestimonialDetailDialog from "./TestimonialDetailDialog";
import TestimonialReviewDialog from "./TestimonialReviewDialog";
import TestimonialFeatureDialog from "./TestimonialFeatureDialog";

const getDisplayName = (name) => {
  if (!name) return null;
  if (typeof name === "string") return name;
  return [name.firstName, name.middleName, name.lastName].filter(Boolean).join(" ") || null;
};

const STATUS_COLORS = {
  PENDING_REVIEW: "warning",
  APPROVED: "success",
  REJECTED: "error",
  DELETED: "default",
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(dateStr));
  } catch {
    return "—";
  }
};

function StatCard({ label, value, color }) {
  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        textAlign: "center",
        borderTop: `3px solid`,
        borderColor: `${color}.main`,
        height: "100%",
      }}
    >
      <Typography variant="h4" fontWeight={700} color={`${color}.main`} p={0} m={0}>
        {value ?? "—"}
      </Typography>
      <Typography variant="body2" color="text.secondary"  p={0} m={0}>
        {label}
      </Typography>
    </Paper>
  );
}

export default function TestimonialsComponent() {
  // Stats
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // List
  const [testimonials, setTestimonials] = useState([]);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);

  // Filters & pagination
  const [statusFilter, setStatusFilter] = useState("");
  const [publishedFilter, setPublishedFilter] = useState("");
  const [topStoryFilter, setTopStoryFilter] = useState("");
  const [userIdFilter, setUserIdFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);

  // Partner name cache: { [userId]: { name, username } }
  const [partnerCache, setPartnerCache] = useState({});

  // Dialogs
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTestimonial, setDetailTestimonial] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null);

  const [featureOpen, setFeatureOpen] = useState(false);
  const [featureTarget, setFeatureTarget] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  const showSnack = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const data = await getTestimonialStats();
      setStats(data);
    } catch {
      // stats are non-critical — silently fail
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Fetch list
  const fetchList = useCallback(async () => {
    try {
      setListLoading(true);
      setListError(null);
      const params = {
        limit: rowsPerPage,
        skip: page * rowsPerPage,
      };
      if (statusFilter) params.status = statusFilter;
      if (publishedFilter !== "") params.isPublished = publishedFilter === "true";
      if (topStoryFilter !== "") params.isTopStory = topStoryFilter === "true";
      if (userIdFilter.trim()) params.userId = userIdFilter.trim();

      const data = await getTestimonialList(params);
      setTestimonials(data.testimonials || []);
      setTotal(data.total || 0);
    } catch (err) {
      setListError(err.message);
    } finally {
      setListLoading(false);
    }
  }, [statusFilter, publishedFilter, topStoryFilter, userIdFilter, page, rowsPerPage]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleRefresh = () => {
    fetchStats();
    fetchList();
  };

  // Open detail dialog with full data
  const handleViewDetail = async (row) => {
    try {
      setDetailLoading(true);
      const data = await getTestimonialById(row._id);
      setDetailTestimonial(data.testimonial);
      setDetailOpen(true);
    } catch (err) {
      showSnack(err.message, "error");
    } finally {
      setDetailLoading(false);
    }
  };

  // Publish
  const handlePublish = async (row) => {
    try {
      await publishTestimonial(row._id);
      showSnack("Testimonial published successfully");
      fetchList();
      fetchStats();
    } catch (err) {
      showSnack(err.message, "error");
    }
  };

  // Unpublish
  const handleUnpublish = async (row) => {
    try {
      await unpublishTestimonial(row._id);
      showSnack("Testimonial unpublished");
      fetchList();
      fetchStats();
    } catch (err) {
      showSnack(err.message, "error");
    }
  };

  // Delete
  const handleDelete = async (row) => {
    if (!window.confirm(`Soft-delete testimonial "${row.title}"?`)) return;
    try {
      await deleteTestimonial(row._id);
      showSnack("Testimonial deleted");
      fetchList();
      fetchStats();
    } catch (err) {
      showSnack(err.message, "error");
    }
  };

  // Review success callback
  const handleReviewSuccess = (result, action) => {
    showSnack(`Testimonial ${action}D successfully`);
    fetchList();
    fetchStats();
  };

  // Feature success callback
  const handleFeatureSuccess = () => {
    showSnack("Feature settings updated");
    fetchList();
    fetchStats();
  };

  const applyFilters = () => {
    setPage(0);
    fetchList();
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 3 }, minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5" fontWeight={700}  p={0} m={0}>
          Testimonials
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={handleRefresh}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Stats Row */}
      {statsLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : stats ? (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard label="Total" value={stats.total} color="primary" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard label="Pending" value={stats.pendingReview} color="warning" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard label="Approved" value={stats.approved} color="success" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard label="Rejected" value={stats.rejected} color="error" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard label="Published" value={stats.published} color="info" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard label="Top Stories" value={stats.topStories} color="secondary" />
          </Grid>
        </Grid>
      ) : null}

      {/* Filters */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} flexWrap="wrap" useFlexGap>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="PENDING_REVIEW">Pending Review</MenuItem>
              <MenuItem value="APPROVED">Approved</MenuItem>
              <MenuItem value="REJECTED">Rejected</MenuItem>
              <MenuItem value="DELETED">Deleted</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Published</InputLabel>
            <Select
              value={publishedFilter}
              label="Published"
              onChange={(e) => setPublishedFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Published</MenuItem>
              <MenuItem value="false">Unpublished</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Top Story</InputLabel>
            <Select
              value={topStoryFilter}
              label="Top Story"
              onChange={(e) => setTopStoryFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Top Story</MenuItem>
              <MenuItem value="false">Not Top Story</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="User ID"
            value={userIdFilter}
            onChange={(e) => setUserIdFilter(e.target.value)}
            sx={{ minWidth: 180 }}
          />

          <Button
            variant="contained"
            size="small"
            startIcon={<TuneRounded />}
            onClick={applyFilters}
          >
            Apply
          </Button>
        </Stack>
      </Paper>

      {/* Error */}
      {listError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {listError}
        </Alert>
      )}

      {/* Table */}
      <Paper elevation={2}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Partner</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Published</TableCell>
                <TableCell>Top Story</TableCell>
                <TableCell>Order</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {listLoading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : testimonials.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary"  p={0} m={0}>
                      No testimonials found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                testimonials.map((row) => (
                  <TableRow key={row._id} hover>
                    {/* User */}
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: "0.8rem" }}>
                          {(getDisplayName(row.user?.name) || row.user?.username || "?")[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 100 }} p={0} m={0}>
                            {getDisplayName(row.user?.name) || row.user?.username || "Unknown"}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Partner */}
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {(() => {
                          const p = row.partner;
                          if (!p) return <Typography variant="body2" color="text.disabled" p={0} m={0}>—</Typography>;
                          // Populated object
                          if (typeof p === "object") {
                            const name = getDisplayName(row.partner?.name) || row.partner?.username || "Unknown";
                            return (
                              <>
                                <Avatar sx={{ width: 28, height: 28, fontSize: "0.8rem" }}>{name[0]}</Avatar>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 100 }} p={0} m={0}>{name}</Typography>
                              </>
                            );
                          }
                        })()}
                      </Box>
                    </TableCell>

                    {/* Title */}
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 160 }} p={0} m={0}>
                        {row.title || "—"}
                      </Typography>
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      <Chip
                        label={row.status}
                        color={STATUS_COLORS[row.status] || "default"}
                        size="small"
                      />
                    </TableCell>

                    {/* Published */}
                    <TableCell>
                      <Chip
                        label={row.isPublished ? "Yes" : "No"}
                        color={row.isPublished ? "info" : "default"}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>

                    {/* Top Story */}
                    <TableCell>
                      {row.isTopStory ? (
                        <Star fontSize="small" color="warning" />
                      ) : (
                        <StarOutline fontSize="small" color="disabled" />
                      )}
                    </TableCell>

                    {/* Featured Order */}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" p={0} m={0}>
                        {row.featuredOrder ?? "—"}
                      </Typography>
                    </TableCell>

                    {/* Submitted */}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" p={0} m={0}>
                        {formatDate(row.createdAt)}
                      </Typography>
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="right">
                      <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
                        {/* View */}
                        <Tooltip title="View Detail">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetail(row)}
                              disabled={detailLoading}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        {/* Review (only for non-deleted) */}
                        {row.status !== "DELETED" && (
                          <Tooltip title="Review">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setReviewTarget(row);
                                setReviewOpen(true);
                              }}
                            >
                              <RateReview fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* Publish / Unpublish */}
                        {row.status === "APPROVED" && !row.isPublished && (
                          <Tooltip title="Publish">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handlePublish(row)}
                            >
                              <Publish fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {row.isPublished && (
                          <Tooltip title="Unpublish">
                            <IconButton
                              size="small"
                              onClick={() => handleUnpublish(row)}
                            >
                              <VisibilityOff fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* Feature settings */}
                        {row.status === "APPROVED" && (
                          <Tooltip title="Feature Settings">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => {
                                setFeatureTarget(row);
                                setFeatureOpen(true);
                              }}
                            >
                              <Star fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* Delete */}
                        {row.status !== "DELETED" && (
                          <Tooltip title="Soft Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(row)}
                            >
                              <DeleteForever fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 30, 50]}
          labelRowsPerPage={
            <Typography
              component="span"
              variant="caption"
              sx={{
                fontWeight: 600,
                letterSpacing: 0.5,
                color: "primary.main",
                textTransform: "uppercase",
                p: 0, mb: 0,
              }}
            >
              Rows per page
            </Typography>
          }
          sx={{
            "& .MuiTablePagination-selectLabel": {
              p: 0,
              m: 0,
            },
            "& .MuiTablePagination-displayedRows": {
              fontWeight: 500,
              color: "text.secondary",
              p: 0, m: 0,
            },
            "& .MuiTablePagination-select": {
              fontWeight: 600,
              color: "primary.main",
              p: 0, m: 0,
            },
          }}
        />
      </Paper>

      {/* Dialogs */}
      <TestimonialDetailDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        testimonial={detailTestimonial}
      />

      <TestimonialReviewDialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        testimonial={reviewTarget}
        onSuccess={handleReviewSuccess}
      />

      <TestimonialFeatureDialog
        open={featureOpen}
        onClose={() => setFeatureOpen(false)}
        testimonial={featureTarget}
        onSuccess={handleFeatureSuccess}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
